import React, { useState, useEffect } from 'react';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from "../../../api/apiService";
import { Spinner } from "react-bootstrap";
import moment from 'moment';
import DeleteModal from '../../../components/Modal/DeleteModal';

const schema = yup.object().shape({
    shift_name: yup.string().required("Shift name is required"),
    start_time: yup
        .string()
        .required("Start time is required")
        .test("valid-time", "Invalid time format", (value) => moment(value, "HH:mm", true).isValid()),
    end_time: yup
        .string()
        .required("End time is required")
        .test("valid-time", "Invalid time format", (value) => moment(value, "HH:mm", true).isValid()),
    // .test(
    //     "is-after-start-time",
    //     "End time must be after start time",
    //     function (value) {
    //         const { start_time } = this.parent;
    //         return moment(value, "HH:mm").isAfter(moment(start_time, "HH:mm"));
    //     }
    // ),
});

const Shifts = () => {

    const [shifts, setShifts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModal, setIsModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentShift, setCurrentShift] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [shiftToDelete, setShiftToDelete] = useState(null);
    const [deleteLoader, setDeleteLoader] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        if (currentShift) {
            reset({
                shift_name: currentShift.shift_name || "",
                start_time: currentShift.start_time ? moment(currentShift.start_time, "HH:mm:ss").format("HH:mm") : "",
                end_time: currentShift.end_time ? moment(currentShift.end_time, "HH:mm:ss").format("HH:mm") : ""
            });
        }
    }, [currentShift, reset]);

    const fetchShiftList = async () => {
        try {
            setIsLoading(true);
            const response = await api.get("/shifts");
            setShifts(response.data.data);
        } catch (error) {
            console.error("Error fetching shifts: ", error);
            toast.error("Failed to fetch shifts");
        } finally {
            setIsLoading(false)
        }
    };

    useEffect(() => {
        fetchShiftList();
    }, [])


    const handleAddShift = () => {
        setIsModal(true);
        setIsEditing(false);
        setCurrentShift({
            shift_name: "",
            start_time: "",
            end_time: ""
        });
    }

    const handleEditShift = (shift) => {
        setCurrentShift(shift);
        setIsModal(true);
        setIsEditing(true);
    }

    const saveShift = async (data) => {
        setIsLoading(true);

        try {
            const formattedData = {
                ...data,
                shift_name: data.shift_name.toLowerCase(),
                start_time: moment(data.start_time, "HH:mm").format("hh:mm A"),
                end_time: moment(data.end_time, "HH:mm").format("hh:mm A"),
            };

            if (isEditing && currentShift) {
                const shiftId = currentShift.id;

                if (!shiftId) {
                    throw new Error("Shift ID is undefined");
                }

                // PUT request for editing
                await api.put(`/shifts/${shiftId}`, formattedData);
                toast.success("Shift updated successfully");
            } else {
                // POST request for adding
                await api.post("/shifts", formattedData);
                toast.success("Shift added successfully");
            }

            fetchShiftList();
            setIsModal(false);
            reset();
        } catch (error) {
            if (error.data && error.data.errors) {
                error.data.errors.forEach(err => {
                    toast.error(err);
                });
            } else {
                toast.error("Error saving shift");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteModal = (shift) => {
        setShiftToDelete(shift);
        setDeleteModal(true);
    };

    const handleDeleteShift = async () => {
        if (!shiftToDelete) return;
        try {
            setDeleteLoader(true);
            await api.delete(`/shifts/${shiftToDelete.id}`);
            setShifts((prev) => prev.filter((shift) => shift.id !== shiftToDelete.id));
            toast.success("Shift deleted successfully");
            setDeleteModal(false);
            setShiftToDelete(null);
        } catch (error) {
            console.error("Error deleting shift: ", error);
            toast.error("Error deleting shift");
        } finally {
            setDeleteLoader(false);
        }
    };


    return (
        <main id="main" className="main">
            <div className="pagetitle">
                <h1>Shifts</h1>
                <nav>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <Link to="/home">Home</Link>
                        </li>
                        <li className="breadcrumb-item active">Shifts</li>
                    </ol>
                </nav>
            </div>

            <section className="section">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-body">
                                {isLoading ? (
                                    <div className="text-center mt-3">
                                        <Spinner animation="border" variant="primary" />
                                        <span className="ms-2">Loading...</span>
                                    </div>
                                ) : (
                                    <>
                                        <h5 className="card-title">Shift Management</h5>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <button className="btn btn-primary" onClick={handleAddShift}>
                                                Add Shift
                                            </button>
                                        </div>
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Start Time</th>
                                                    <th>End Time</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {shifts?.map((shift, index) => (
                                                    <tr key={index}>
                                                        <td className='text-capitalize'>{shift?.shift_name}</td>
                                                        <td>{moment(shift?.start_time, 'HH:mm:ss').format('h:mm A')}</td>
                                                        <td>{moment(shift?.end_time, 'HH:mm:ss').format('h:mm A')}</td>
                                                        <td>
                                                            <button
                                                                className="btn btn-warning btn-sm me-2"
                                                                onClick={() => handleEditShift(shift)}
                                                                data-bs-toggle="tooltip"
                                                                data-bs-placement="top"
                                                                title="Edit Shift"
                                                            >
                                                                <i className="bi bi-pencil-square"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-danger btn-sm"
                                                                onClick={() => openDeleteModal(shift)}
                                                                data-bs-toggle="tooltip"
                                                                data-bs-placement="top"
                                                                title="Delete Shift"
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {isModal && (
                <>
                    <div className='modal fade show d-block' style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        {isEditing ? "Edit Shift" : "Add Shift"}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setIsModal(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <form onSubmit={handleSubmit(saveShift)}>
                                        <div className="mb-3">
                                            <label className="form-label">Shift Name</label>
                                            <input
                                                type="text"
                                                className={`form-control ${errors.shift_name ? "is-invalid" : ""}`}
                                                {...register("shift_name")}
                                            />
                                            {errors.shift_name && <div className="invalid-feedback">{errors.shift_name.message}</div>}
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Start Time (HH:mm)</label>
                                            <input
                                                type="time"
                                                className={`form-control ${errors.start_time ? "is-invalid" : ""}`}
                                                {...register("start_time")}
                                            />
                                            {errors.start_time && <div className="invalid-feedback">{errors.start_time.message}</div>}
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">End Time (HH:mm)</label>
                                            <input
                                                type="time"
                                                className={`form-control ${errors.end_time ? "is-invalid" : ""}`}
                                                {...register("end_time")}
                                            />
                                            {errors.end_time && <div className="invalid-feedback">{errors.end_time.message}</div>}
                                        </div>
                                        <div className="d-flex justify-content-end">
                                            <button type="button" className="btn btn-secondary me-2" onClick={() => setIsModal(false)}>
                                                Cancel
                                            </button>
                                            <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                                {isLoading ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-2" />
                                                        {isEditing ? "Updating..." : "Saving..."}
                                                    </>
                                                ) : (
                                                    isEditing ? "Update" : "Save"
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {deleteModal && (
                <DeleteModal
                    show={deleteModal}
                    onClose={() => setDeleteModal(false)}
                    onDelete={handleDeleteShift}
                    isLoading={deleteLoader}
                    body={
                        <>
                            Are you sure you want to delete the shift <strong className="text-capitalize">"{shiftToDelete?.shift_name}"</strong>?
                        </>
                    }
                />
            )}

        </main>
    );
};

export default Shifts;